import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { ArrowRight2, InfoCircle } from "iconsax-react";
import { Button, ModalFormSplit } from "@/shared/ui";
import { formatDateLong } from "@/shared/utils";
import type { Locale } from "@/shared/utils";
import type { BudgetSubcategory, BudgetItem, MoneyString } from "../../../types";
import { formatCurrencyNoDecimals, clampProgressWidth, removeSectionNumber, SUBCATEGORY_ICONS } from "../../../utils";
import { getSubcategoryColorClasses, getSubcategoryColorHex } from "../../../utils/consts";
import { getLocalizedText } from "../../../utils/localization";
import ItemDetailPanel from "./ItemDetailPanel";

interface Props {
  subcategory: BudgetSubcategory;
  selectedItem: BudgetItem | null;
  isExpanded: boolean;
  onItemClick: (item: BudgetItem) => void;
  onCloseDetail: () => void;
  onClose: () => void;
  hidePlanned?: boolean;
}

interface OperationRow {
  groupId: string;
  groupName: string;
  total: MoneyString;
  account: string;
  date: string;
  items: BudgetItem[];
}

interface OperationsByDate {
  date: string;
  operations: OperationRow[];
}

function aggregateOperations(items: BudgetItem[], locale: string): OperationRow[] {
  const map = new Map<string, { items: BudgetItem[]; total: number; date: string }>();
  items.forEach((item) => {
    const existing = map.get(item.group_id);
    if (existing) {
      existing.items.push(item);
      existing.total += parseFloat(item.amount) || 0;
      if (item.date > existing.date) existing.date = item.date;
    } else {
      map.set(item.group_id, { items: [item], total: parseFloat(item.amount) || 0, date: item.date });
    }
  });
  return Array.from(map.entries()).map(([groupId, { items: groupItems, total, date }]) => ({
    groupId,
    groupName: getLocalizedText(groupItems[0].group_name, locale).split(" - ")[0],
    total: total.toFixed(2) as MoneyString,
    account: groupItems[0].account,
    date,
    items: groupItems,
  }));
}

function groupOperationsByDate(operations: OperationRow[]): OperationsByDate[] {
  const map = new Map<string, OperationRow[]>();
  operations.forEach((operation) => {
    const existing = map.get(operation.date) || [];
    existing.push(operation);
    map.set(operation.date, existing);
  });
  return Array.from(map.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, dateOperations]) => ({ date, operations: dateOperations }));
}

export default function SubcategoryDrawer({
  subcategory,
  selectedItem,
  isExpanded,
  onItemClick,
  onCloseDetail,
  onClose,
  hidePlanned = false,
}: Props) {
  const { t, i18n } = useTranslation("FinancePage");
  const locale = (i18n.language || "ru") as Locale;
  const { fill, subtle } = getSubcategoryColorClasses(subcategory.id, 0);
  const iconHex = getSubcategoryColorHex(subcategory.id, 0);
  const Icon = SUBCATEGORY_ICONS[subcategory.id];

  const operations = useMemo(() => aggregateOperations(subcategory.items, locale), [subcategory.items, locale]);

  const groupedByDate = useMemo(() => groupOperationsByDate(operations), [operations]);

  function handleClose() {
    if (isExpanded) {
      onCloseDetail();
    } else {
      onClose();
    }
  }

  return (
    <ModalFormSplit
      icon={Icon ?? null}
      iconBgClassName={fill}
      secondIcon={InfoCircle}
      secondIconBgClassName="background-neutral-fill"
      title={removeSectionNumber(getLocalizedText(subcategory.name, locale))}
      onClose={handleClose}
      secondaryTitle={isExpanded ? t("detail.title") : undefined}
      onSecondaryClose={isExpanded ? onCloseDetail : undefined}
      secondaryContent={
        isExpanded && selectedItem ? (
          <ItemDetailPanel
            item={selectedItem}
            subcategory={subcategory}
            iconBgClassName={subtle}
            icon={Icon ? <Icon size={20} color={iconHex} /> : null}
          />
        ) : undefined
      }
      footer={
        <Button
          variant="tertiary"
          onClick={handleClose}
          className="w-full py-2.5 radius-md border surface-base-stroke text-body-bold-md content-base-primary">
          {t("drawer.close")}
        </Button>
      }>
      <div className="p-5 radius-lg border surface-component-stroke">
        <div className="text-label-sm content-action-neutral mb-1">{t("drawer.allExpenses")}</div>
        <div className={cn("flex items-baseline justify-between", hidePlanned ? "mb-0" : "mb-5")}>
          <div className="flex items-end gap-1">
            <span className="text-display-sm content-base-primary">
              {formatCurrencyNoDecimals(subcategory.total, false)}
            </span>
            <span className="text-display-xs content-base-secondary">₸</span>
          </div>
          {!hidePlanned && (
            <span className="text-heading-lg content-base-low">{subcategory.execution_percentage.toFixed(0)}%</span>
          )}
        </div>
        {!hidePlanned && (
          <>
            <div className="flex gap-0.5 mb-3">
              <div
                className={cn("h-1.5 radius-2xs", fill)}
                style={{ width: `${clampProgressWidth(subcategory.execution_percentage)}%` }}
              />
              <div className="h-1.5 radius-2xs surface-component-stroke-fill flex-1" />
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-label-xs content-base-secondary">{t("section.planned")}</span>
                <span className="text-body-bold-sm content-base-primary">
                  {formatCurrencyNoDecimals(subcategory.planned)}
                </span>
              </div>
              <div className="flex flex-col gap-1 text-right">
                <span className="text-label-xs content-base-secondary">{t("section.remaining")}</span>
                <span className="text-body-bold-sm content-base-primary">
                  {formatCurrencyNoDecimals(subcategory.remaining)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {groupedByDate.map((dateGroup) => (
        <div key={dateGroup.date}>
          <div className="flex justify-between items-center mb-3">
            <div className="text-label-sm content-base-primary">{formatDateLong(dateGroup.date, locale)}</div>
            <div className="text-label-xs">
              <span className="content-base-primary">{dateGroup.operations.length}</span>{" "}
              <span className="content-base-secondary">{t("drawer.operationsLabel")}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {dateGroup.operations.map((operation) => {
              const isSelected = selectedItem?.group_id === operation.groupId;

              return (
                <button
                  key={operation.groupId}
                  type="button"
                  onClick={() => onItemClick(operation.items[0])}
                  className={cn(
                    "flex justify-between items-center gap-3 p-1.5 radius-md text-left w-full cursor-pointer outline-none",
                    isSelected && "surface-component-fill",
                  )}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={cn("w-10 h-10 radius-xs flex items-center justify-center shrink-0", subtle)}>
                      {Icon && <Icon size={20} color={iconHex} />}
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="text-label-sm content-base-primary line-clamp-1">{operation.groupName}</div>
                      <div className="text-body-regular-xs content-base-secondary truncate">{operation.account}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="flex flex-col gap-1 text-right">
                      <div className="text-body-bold-sm content-base-primary">
                        - {formatCurrencyNoDecimals(operation.total)}
                      </div>
                    </div>
                    <span className="content-base-secondary">
                      <ArrowRight2 size={16} color="currentColor" className="currentColor" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </ModalFormSplit>
  );
}
